import { Fragment } from 'react';
import type { XeroReportResponse } from '../api.js';
import type { Translate } from '../i18n/index.js';
import { theme } from '../theme.js';
import { Card } from '../components/ui.js';
import { BarList, ColumnChart } from '../components/Chart.js';

/**
 * A Xero report, drawn as Xero sent it.
 *
 * Nothing here is re-totalled and nothing is translated. The figures are
 * Xero's arithmetic on the client's own ledger — deriving them a second time
 * would create a second answer to a question that must only have one — and
 * the account names are the client's own words for their own accounts, which
 * are not ours to rewrite. Only the furniture around them is translated.
 *
 * The shape comes flattened from the API; see api/src/domain/xeroReport.ts for
 * why that is not done here. Every report Xero publishes arrives in that one
 * shape, which is why this draws the profit and loss, the balance sheet and
 * the aged detail without knowing which it has.
 *
 * WHAT `charts` IS FOR
 * The two charts above the table read the report structurally — the last
 * total row as the bottom line, multi-row sections as its components. That
 * reasoning is sound on a profit and loss and nonsense on a balance sheet,
 * where the final total is assets equalling liabilities plus equity and
 * charting it says nothing. So the caller says whether its report is the
 * kind that has a bottom line worth drawing, rather than this guessing.
 */

export function XeroReportTable({
  data,
  charts = true,
  t,
}: {
  data: XeroReportResponse;
  /** Whether the report has a bottom line worth charting. */
  charts?: boolean;
  t: Translate;
}) {
  const { report } = data;

  const cell = {
    padding: '7px 12px',
    fontSize: '13px',
    borderBottom: `1px solid ${theme.dividerSoft}`,
  };

  const figure = {
    ...cell,
    textAlign: 'end' as const,
    fontFamily: theme.fontMono,
    whiteSpace: 'nowrap' as const,
    // Figures read left to right even on an Arabic page: a minus sign that
    // migrates to the other end of a number changes what it says.
    direction: 'ltr' as const,
  };

  /*
    The bottom line, period by period.

    Found rather than assumed: Xero names the final total differently by
    region and by layout, so the last total row of the report is taken
    rather than a row matching "Net Profit". If the report has no total at
    all there is nothing to chart and the chart is not drawn.
  */
  const bottomLine = report.sections
    .flatMap((section) => section.rows)
    .filter((row) => row.kind === 'total')
    .at(-1);

  const parse = (value: string): number => {
    const cleaned = value.replace(/[^\d.,-]/g, '').replace(/,/g, '');
    const n = Number.parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  /* Xero returns the newest period first; a trend reads oldest to newest. */
  const trend =
    bottomLine && report.columns.length > 1
      ? report.columns
          .map((column, i) => ({ label: column, value: parse(bottomLine.values[i] ?? '0') }))
          .reverse()
      : [];

  /*
    One period is not a trend, and a card with nothing in it is worse than
    no card. What a single column can show instead is where the money went:
    the section totals beside each other — income, what it cost, what it
    cost to run.

    The bottom line is left out of this one. It is a balance, not a part of
    the whole, and a bar for it beside the parts would be double counting
    — besides which a loss is negative and these bars measure magnitude.
    The table shows it in bold two inches below.
  */
  const sectionTotals = report.sections
    /*
      Only sections that total something up.

      Xero emits a derived balance — Gross Profit, Net Profit — as a section
      containing nothing but its own total. Those are not components, they
      are the arithmetic on the components, and charting Gross Profit beside
      the income and cost of sales it is made of counts the same money
      twice.

      Told apart structurally rather than by name: a section with one row is
      a balance, a section with several is a group of things. Matching on
      "Gross Profit" would work in English and fail the moment a client's
      Xero is set to another region's layout.
    */
    .filter((section) => section.rows.length > 1)
    .flatMap((section) => section.rows)
    .filter((row) => row.kind === 'total' && row !== bottomLine)
    .map((row) => ({ label: row.label, value: parse(row.values[0] ?? '0') }))
    .filter((row) => row.value !== 0);

  if (report.sections.length === 0) {
    return (
      <Card>
        <div style={{ padding: '18px', fontSize: '13px', color: theme.textMuted }}>
          {t('Xero returned no figures for this period.')}
        </div>
      </Card>
    );
  }

  return (
    <>
      {charts && trend.length <= 1 && sectionTotals.length > 1 && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {t('Where the money went')}
          </div>
          {/* One series, so one hue and no legend — the labels carry it. */}
          <BarList
            rows={sectionTotals.map((row) => ({ ...row, step: 2 }))}
            currency={data.currency}
            t={t}
          />
        </Card>
      )}

      {charts && trend.length > 1 && (
        <Card style={{ marginBottom: '14px' }}>
          <div
            style={{
              padding: '12px 16px',
              borderBottom: `1px solid ${theme.dividerSoft}`,
              fontWeight: 600,
              fontSize: '13px',
              background: theme.surfaceHeader,
            }}
          >
            {bottomLine?.label || t('The bottom line')}
          </div>
          {/*
            Green above the line and red below it, which is the firm's own
            pair. As colour alone those two are not distinguishable to a
            red-green colourblind reader, so they are never asked to be:
            the side of the zero line a column is on says it, and every
            column prints its own figure.
          */}
          <ColumnChart columns={trend} currency={data.currency} t={t} />
        </Card>
      )}

    <Card>
      <div
        style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.dividerSoft}`,
          background: theme.surfaceHeader,
        }}
      >
        {/* Xero's own titles — report name, organisation, period. Its words
            for its own report, so they are shown rather than reconstructed. */}
        {report.titles.map((title, i) => (
          <div
            key={title}
            style={{
              fontSize: i === 0 ? '14px' : '12.5px',
              fontWeight: i === 0 ? 700 : 400,
              color: i === 0 ? theme.text : theme.textMuted,
            }}
          >
            {title}
          </div>
        ))}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        {report.columns.length > 0 && (
          <thead>
            <tr>
              <th
                style={{
                  ...cell,
                  textAlign: 'start',
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '.04em',
                  color: theme.label,
                }}
              />
              {report.columns.map((column) => (
                <th
                  key={column}
                  style={{
                    ...figure,
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '.04em',
                    color: theme.label,
                  }}
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
        )}

        <tbody>
          {report.sections.map((section, sectionIndex) => (
            <Fragment key={`${section.title}-${sectionIndex}`}>
              {section.title && (
                <tr key={`${section.title}-head`}>
                  <td
                    colSpan={report.columns.length + 1}
                    style={{
                      ...cell,
                      paddingTop: sectionIndex === 0 ? '12px' : '20px',
                      fontWeight: 700,
                      fontSize: '12.5px',
                    }}
                  >
                    {section.title}
                  </td>
                </tr>
              )}

              {section.rows.map((row, rowIndex) => (
                <tr key={`${section.title}-${row.label}-${rowIndex}`}>
                  <td
                    style={{
                      ...cell,
                      fontWeight: row.kind === 'total' ? 700 : 400,
                      paddingInlineStart: row.kind === 'total' ? '12px' : '26px',
                    }}
                  >
                    {row.label}
                  </td>
                  {row.values.map((value, i) => (
                    <td
                      key={`${row.label}-${i}`}
                      style={{ ...figure, fontWeight: row.kind === 'total' ? 700 : 400 }}
                    >
                      {value}
                    </td>
                  ))}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>

      <div style={{ padding: '11px 16px', fontSize: '11.5px', color: theme.textMuted }}>
        {t('Prepared by Xero. These are its figures, shown unchanged.')}
      </div>
    </Card>
    </>
  );
}
